/**
 * Página: CentroReportes — Unifica Reportes F2F (Salud), DRS y Respaldos
 * en un solo dashboard con navegación por pestañas (mismo patrón que CentroOperaciones).
 */
import React, { useMemo, useState } from 'react';
import { Activity, Shield, FileText } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import Monitoreo from './Monitoreo';
import ReporteDRS from './ReporteDRS';
import Respaldos from './Respaldos';

const TABS = [
  { key: 'salud', label: 'Reportes F2F', icon: Activity, permission: 'ops.monitoreo.view', Component: Monitoreo },
  { key: 'drs', label: 'Reportes DRS', icon: Shield, permission: 'ops.monitoreo.view', Component: ReporteDRS },
  { key: 'respaldos', label: 'Respaldos', icon: FileText, permission: 'ops.monitoreo.view', Component: Respaldos }
];

export default function CentroReportes() {
  const { hasPermission } = useAuth();

  const availableTabs = useMemo(
    () => TABS.filter((tab) => !tab.permission || hasPermission(tab.permission)),
    [hasPermission]
  );

  const [activeKey, setActiveKey] = useState(availableTabs[0]?.key);
  const activeTab = availableTabs.find((t) => t.key === activeKey) || availableTabs[0];

  const themeClasses = 'bg-[radial-gradient(circle_at_top_right,rgba(124,58,237,0.22),transparent_30%),radial-gradient(circle_at_bottom_left,rgba(34,211,238,0.12),transparent_25%),linear-gradient(135deg,#140728_0%,#0b041a_50%,#1a0b36_100%)] text-white';

  if (!activeTab) {
    return (
      <div className={`min-h-screen ${themeClasses} flex items-center justify-center`}>
        <p className="text-sm text-violet-200/70">No tienes permisos para ver módulos de Centro de Reportes.</p>
      </div>
    );
  }

  const ActiveComponent = activeTab.Component;

  return (
    <div className={`min-h-screen ${themeClasses} px-4 pb-8 pt-6 font-sans md:px-6 xl:px-8`}>
      <div className="mb-6 flex items-center gap-3">
        <div className="rounded-2xl border border-violet-400/30 bg-violet-500/20 p-3">
          <FileText className="h-6 w-6 text-violet-200" />
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-violet-200/75">Kloud Ops</p>
          <h1 className="mt-1 text-2xl font-bold text-violet-100">Centro de Reportes</h1>
          <p className="text-sm text-violet-200/70">Reportes de salud F2F, DRS y respaldos AWS/Azure en un solo lugar.</p>
        </div>
      </div>

      <div className="mb-5 flex flex-wrap items-center gap-2">
        {availableTabs.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab.key === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveKey(tab.key)}
              className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-medium transition-all ${
                active
                  ? 'border-violet-300/60 bg-violet-500/25 text-violet-50 shadow-[0_0_18px_rgba(139,92,246,0.3)]'
                  : 'border-violet-500/15 bg-black/20 text-violet-200/60 hover:border-violet-400/40 hover:text-violet-100'
              }`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      <ActiveComponent />
    </div>
  );
}
